// WP-A · Arrival form: guest names + age (1–17, 18+ default), flights in/out
// → arrivals/{id}. Sent at PREPARE (-7 days). A reopened link loads the booking
// server-side (loadBookingByToken) and prefills the saved flights + guest
// list so the guest can review or change what they already sent — the token
// payload alone is a stale snapshot after the first submit.
//
// ONE REALITY (OBR-000064 — VRBO sends no modification emails, so the guest
// is the only reliable channel when the headcount changes): the guest-list
// rows are the single state; the Adults/Children steppers are a VIEW on that
// same array (lib/guests.js). Change a count → rows appear/disappear; add,
// remove, or re-band a row → the counts move. The two can never diverge.
// The server re-derives adults/children from the submitted list and ignores
// any client-sent counts (intake.helpers.map_arrival_updates).
//
// FREEZE: at stage sesamed (and beyond/terminal) the party is set — counts +
// guest list render as a TEXT summary (session-34 rule: confirmed data is
// text, never locked inputs) and the guest cannot edit them; flights stay
// editable. The security rules force every arrivals/ doc to carry a guests
// list, so the frozen submit echoes the unmodifiable server-loaded rows and
// the intake CF drops them (intake.processor) — only the flights take effect.
import { useState, useEffect } from "preact/hooks";
import { MAX_GUESTS, AGES } from "../config.js";
import { validateArrival } from "../lib/validation.js";
import { submitArrival } from "../lib/firestore.js";
import { describeError, reportError, SUPPORT_PHONE } from "../lib/errlog.js";
import { loadBookingByToken } from "../lib/api.js";
import { fmtDate, parseGuestList } from "../lib/dates.js";
import {
  ADULT,
  CHILD,
  MIN_ADULTS,
  bandOf,
  canRemoveRow,
  changeBandCount,
  countByBand,
  isFrozenStage,
  skeletonGuests,
} from "../lib/guests.js";
import { TextField, SelectField, MissingBanner } from "./Fields.jsx";
import { Status } from "./Status.jsx";

const MISSING_LABELS = {
  booking_ref: "your booking reference",
  guests: "every guest's name (and age for anyone under 18)",
};

export function ArrivalForm({ booking_ref, lead_ref, email, phone_e164, arrival_token, first_name, last_name, checkin_date, checkout_date, adults, children, onDone }) {
  const [summary, setSummary] = useState({
    first: first_name || "",
    last: last_name || "",
    checkin: checkin_date || "",
    checkout: checkout_date || "",
  });
  const guestSummary = [
    summary.first, summary.last,
  ].filter(Boolean).join(" ") + " · " + fmtDate(summary.checkin) + " – " + fmtDate(summary.checkout);

  const [form, setForm] = useState({
    booking_ref: booking_ref || "",
    flight_in: "",
    flight_out: "",
  });
  // The single source of truth for the party. Counts are derived below.
  const [guests, setGuests] = useState(() => skeletonGuests(adults, children));
  const [frozen, setFrozen] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const counts = countByBand(guests);

  // Reopen path: replace the token snapshot with the server truth (dates,
  // saved flights, saved guest list, REFRESHED counts, stage). Best-effort —
  // the token data stays if the load fails, so the form never dead-ends
  // offline (the server then enforces the freeze on submit).
  useEffect(() => {
    if (!arrival_token) return;
    let cancelled = false;
    loadBookingByToken(arrival_token)
      .then((b) => {
        if (cancelled || !b || !b.booking_id) return;
        setSummary({
          first: b.first_name || "",
          last: b.last_name || "",
          checkin: b.checkin_date || "",
          checkout: b.checkout_date || "",
        });
        setForm((f) => ({
          ...f,
          flight_in: b.flight_in || f.flight_in,
          flight_out: b.flight_out || f.flight_out,
        }));
        if (isFrozenStage(b.stage)) setFrozen(true);
        const saved = parseGuestList(b.guest_list);
        const serverHasCounts = [b.adults, b.children].some((v) => v !== "" && v != null);
        if (saved.length) {
          // A saved list is the truth — counts derive from its ages.
          setGuests(saved.slice(0, MAX_GUESTS));
        } else if (serverHasCounts) {
          // No saved list yet: the server's refreshed counts beat the stale
          // token snapshot (e.g. VRBO changed 1 guest → 10 adults + 6 kids).
          setGuests(skeletonGuests(b.adults, b.children));
        }
      })
      .catch(() => { /* token payload stays */ });
    return () => { cancelled = true; };
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  }
  function setGuest(i, field, value) {
    setGuests((gs) => gs.map((g, idx) => (idx === i ? { ...g, [field]: value } : g)));
    setErrors((e) => (e.guests ? { ...e, guests: undefined } : e));
  }
  function addGuest() {
    if (guests.length >= MAX_GUESTS) return;
    setGuests((gs) => [...gs, { name: "", age: "18+" }]);
  }
  function removeGuest(i) {
    if (!canRemoveRow(guests, i)) return;
    setGuests((gs) => gs.filter((_, idx) => idx !== i));
  }
  function stepCount(band, delta) {
    setGuests((gs) => changeBandCount(gs, band, delta));
    setErrors((e) => (e.guests ? { ...e, guests: undefined } : e));
  }

  async function submit(e) {
    e.preventDefault();
    // Frozen (sesamed+): the party is set — the list renders as text and is
    // not guest-editable. The doc still carries the (server-loaded) rows
    // because the security rules require a guests list; the intake CF drops
    // guest fields at sesamed, so only the flights take effect.
    const payload = { ...form, guests, frozen, lead_ref, email, phone_e164, arrival_token };
    const { errors: e2 } = validateArrival({ ...form, guests: frozen ? null : guests });
    setErrors(e2);
    if (Object.keys(e2).length > 0) return;
    setBusy(true);
    setStatus(null);
    try {
      const res = await submitArrival(payload);
      onDone?.({ id: res.id });
    } catch (err) {
      reportError("arrival_submit", err);
      setStatus({ kind: "err", text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  const ageOptions = AGES.map((a) => ({ value: a, label: a }));
  const atCap = guests.length >= MAX_GUESTS;

  return (
    <form class="surface" onSubmit={submit}>
      <h2>Arrival details</h2>
      {/* Booking identity is TEXT, not a locked input (session 34: readOnly
          fields look editable but refuse input — reads as "page is broken"). */}
      <div class="booking-summary">
        <p class="summary-name">Booking</p>
        <p class="summary-line">{guestSummary}</p>
      </div>
      <div class="row">
        <TextField label="Flight in" field="flight_in" value={form.flight_in} onInput={set} placeholder="AA123 3:45pm" />
        <TextField label="Flight out" field="flight_out" value={form.flight_out} onInput={set} placeholder="AA456 11:00am" />
      </div>
      {frozen ? (
        /* sesamed and beyond: the party is set — TEXT, never locked inputs. */
        <div class="booking-summary" data-testid="frozen-party">
          <p class="summary-name">
            Guests: {counts.adults} adult{counts.adults === 1 ? "" : "s"}
            {counts.children ? ` · ${counts.children} child${counts.children === 1 ? "" : "ren"}` : ""}
          </p>
          {guests.filter((g) => g.name && g.name.trim()).map((g, i) => (
            <p class="summary-line" key={i}>{g.name}{g.age ? ` (${g.age})` : ""}</p>
          ))}
          <p class="summary-line">Your guest list is set — our cleaners have prepared for exactly this many people. Need a change? Text Julie at {SUPPORT_PHONE}.</p>
        </div>
      ) : (
        <fieldset class="guests">
          <legend>Guests ({guests.length}/{MAX_GUESTS})</legend>
          <p class="band hint">Our cleaners prepare for exactly this many people — please count everyone, children included. Not sure of everyone yet, or flights not booked? Send what you know. You can reopen this link and update it anytime before your stay.</p>
          <div class="row count-steppers">
            <div class="stepper" data-testid="adults-stepper">
              <span class="field-label">Adults (18+)</span>
              <div class="stepper-controls">
                <button type="button" class="stepper-btn" aria-label="Fewer adults" disabled={counts.adults <= MIN_ADULTS} onClick={() => stepCount(ADULT, -1)}>−</button>
                <span class="stepper-value" data-testid="adults-count">{counts.adults}</span>
                <button type="button" class="stepper-btn" aria-label="More adults" disabled={atCap} onClick={() => stepCount(ADULT, 1)}>+</button>
              </div>
            </div>
            <div class="stepper" data-testid="children-stepper">
              <span class="field-label">Children (1–17)</span>
              <div class="stepper-controls">
                <button type="button" class="stepper-btn" aria-label="Fewer children" disabled={counts.children <= 0} onClick={() => stepCount(CHILD, -1)}>−</button>
                <span class="stepper-value" data-testid="children-count">{counts.children}</span>
                <button type="button" class="stepper-btn" aria-label="More children" disabled={atCap} onClick={() => stepCount(CHILD, 1)}>+</button>
              </div>
            </div>
          </div>
          {guests.map((g, i) => (
            <div class="row" key={i}>
              <TextField label={`Guest ${i + 1} name`} field={`name${i}`} value={g.name} onInput={(_, v) => setGuest(i, "name", v)} required />
              <SelectField label="Age" field={`age${i}`} value={g.age} onChange={(_, v) => setGuest(i, "age", v)} options={ageOptions} placeholder={g.age ? undefined : "Select age"} required />
              {canRemoveRow(guests, i) ? <button type="button" class="link" onClick={() => removeGuest(i)}>Remove</button> : null}
            </div>
          ))}
          {errors.guests ? <span class="field-error">{errors.guests}</span> : null}
          {!atCap ? <button type="button" class="link" onClick={addGuest}>+ Add guest</button> : null}
        </fieldset>
      )}
      <MissingBanner errors={errors} labels={MISSING_LABELS} />
      <button type="submit" disabled={busy}>{busy ? "Sending..." : "Submit arrival details"}</button>
      {status ? <Status kind={status.kind}>{status.text}</Status> : null}
    </form>
  );
}
